// Settings › Data and privacy (Console D, views/settings.js `#/settings/privacy`; APS-320), with
// erasing an app user (plan step 3.6; D92): erasing one of the project's app users by the
// hash the app passes to setUser (`user.hash`). Owners and admins schedule it; it runs a day
// later, cancelable until then ("scheduled until"), and deletes every record of the project that
// carries the hash and every session that does, whole. The hash shows while the erasure is open
// and is forgotten after. Members see that only owners and admins can do this.
import { useQuery } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { isApiError } from '../api/client'
import {
  orgQuery,
  plansQuery,
  projectsQuery,
  useCancelUserErasure,
  userErasuresQuery,
  useScheduleUserErasure,
} from '../api/queries'
import type { UserErasure } from '../api/types'
import { Confirm } from '../components/Confirm'
import { EmptyNote, PageHead, Section, Status, type StatusKey, Table } from '../components/d'
import { ErrorState, Loading } from '../components/states'
import { PLAN_KEY } from '../components/TrialMeter'
import { type Translator, useI18n } from '../i18n'
import { day, dayTime } from '../lib/format'
import { retention } from './PlanSettings'

/** The hash as the app sends it: the API's own rule, checked here to say so before sending. */
const HASH = /^[A-Za-z0-9+/=_.:-]{1,256}$/
const EMAIL_LIKE = /[^\s@]+@[^\s@]+\.[^\s@]+/

/** An erasure's state as a D status: scheduled ◐, erased ✓, canceled ○, under way ○. */
const STATUS: Record<UserErasure['status'], StatusKey> = {
  scheduled: 'reg',
  done: 'res',
  canceled: 'mute',
  running: 'on',
  failed: 'on',
}

function status(e: UserErasure, i: Translator): string {
  const { t } = i
  // Until when it can be canceled, to the minute; what happened, to the day (the mock's statuses).
  if (e.status === 'scheduled')
    return t('settings.erase.status.scheduled', { date: dayTime(i, e.scheduled_until) })
  if (e.status === 'done') return t('settings.erase.status.done', { date: day(i, e.done_at ?? '') })
  if (e.status === 'canceled')
    return t('settings.erase.status.canceled', { date: day(i, e.canceled_at ?? '') })
  return t('settings.erase.status.running')
}

function ScheduleForm({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t, date, rich } = useI18n()
  const schedule = useScheduleUserErasure(orgId, projectId)
  const [hash, setHash] = useState('')
  const [reason, setReason] = useState('')
  const ids = { hash: useId(), reason: useId(), help: useId() }
  const ready = HASH.test(hash.trim()) && reason.trim() !== '' && !EMAIL_LIKE.test(reason)
  return (
    <form className="se-form" onSubmit={(e) => e.preventDefault()}>
      <label className="lbl" htmlFor={ids.hash}>
        <span>{rich('settings.erase.hash')}</span>
        <input
          id={ids.hash}
          className="input mono"
          translate="no"
          autoComplete="off"
          spellCheck={false}
          maxLength={256}
          value={hash}
          onChange={(e) => setHash(e.target.value)}
        />
      </label>
      <div className="lbl">
        <label htmlFor={ids.reason}>{t('settings.erase.reason')}</label>
        <input
          id={ids.reason}
          className="input"
          maxLength={1000}
          aria-describedby={ids.help}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <span id={ids.help} className="se-hint">
          {t('settings.erase.reasonHint')}
        </span>
      </div>
      <div className="se-row">
        <Confirm
          buttonClass="btn danger"
          label={t('settings.erase.submit')}
          title={t('settings.erase.confirmTitle')}
          body={t('settings.erase.confirmBody')}
          confirm={t('settings.erase.confirm')}
          busy={schedule.isPending}
          disabled={!ready}
          onConfirm={() =>
            schedule.mutate(
              { user_hash: hash.trim(), reason: reason.trim() },
              {
                onSuccess: () => {
                  setHash('')
                  setReason('')
                },
              },
            )
          }
        />
      </div>
      {schedule.isSuccess ? (
        <p role="status" className="se-hint">
          <Status
            s="ok"
            label={t('settings.erase.scheduled', {
              date: date(schedule.data.scheduled_until, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }),
            })}
          />
        </p>
      ) : null}
      {schedule.error ? (
        <p role="alert" className="se-hint">
          <Status
            s="miss"
            label={
              isApiError(schedule.error, 400)
                ? t('settings.erase.invalid')
                : isApiError(schedule.error, 409)
                  ? t('settings.erase.exists')
                  : isApiError(schedule.error, 403)
                    ? t('settings.adminOnly')
                    : isApiError(schedule.error, 429)
                      ? t('settings.erase.tooMany')
                      : t('state.failed')
            }
          />
        </p>
      ) : null}
    </form>
  )
}

function Erasures({ orgId, projectId }: { orgId: string; projectId: string }) {
  const i = useI18n()
  const { t } = i
  const list = useQuery(userErasuresQuery(orgId, projectId))
  const cancel = useCancelUserErasure(orgId, projectId)
  const rows = list.data?.erasures ?? []
  return (
    <>
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorState error={list.error} retry={() => list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyNote title={t('settings.erase.none')} />
      ) : (
        <Table label={t('table.of', { name: t('settings.erase.list') })}>
          <thead>
            <tr>
              <th className="f">{t('settings.erase.col.user')}</th>
              <th>{t('settings.erase.col.status')}</th>
              <th className="last">
                <span className="visually-hidden">{t('settings.keys.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id}>
                <td className="f">
                  {e.user_hash ? (
                    <code translate="no">{e.user_hash}</code>
                  ) : (
                    <span className="faint">{t('settings.erase.forgotten')}</span>
                  )}
                  <span className="t2 prose">
                    {t(e.by_support ? 'settings.erase.askedSupport' : 'settings.erase.askedOrg', {
                      date: day(i, e.requested_at),
                    })}
                  </span>
                </td>
                <td>
                  <Status s={STATUS[e.status]} label={status(e, i)} />
                </td>
                <td className="last">
                  {e.status === 'scheduled' && !e.by_support ? (
                    <Confirm
                      confirmClass="btn solid"
                      buttonClass="btn ghost sm"
                      label={t('settings.erase.cancel')}
                      title={t('settings.erase.cancelTitle')}
                      body={t('settings.erase.cancelBody')}
                      confirm={t('settings.erase.cancelConfirm')}
                      busy={cancel.isPending}
                      onConfirm={() => cancel.mutate(e.id)}
                    />
                  ) : e.status === 'scheduled' ? (
                    <span className="se-hint">{t('settings.erase.supportOnly')}</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {cancel.error ? (
        <p role="alert" className="se-hint">
          <Status
            s="miss"
            label={isApiError(cancel.error, 409) ? t('settings.erase.tooLate') : t('state.failed')}
          />
        </p>
      ) : null}
    </>
  )
}

export function PrivacyPage({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: string
  title: string
}) {
  const { t, rich } = useI18n()
  const org = useQuery(orgQuery(orgId))
  const projects = useQuery(projectsQuery(orgId))
  const plans = useQuery(plansQuery)
  const project = (Array.isArray(projects.data) ? projects.data : []).find(
    (p) => p.id === projectId,
  )
  const canErase = org.data?.role === 'owner' || org.data?.role === 'admin'
  // What a trial falls to (D107): Free's retention, from the plans the API lists.
  const free = plans.data?.plans?.find((p) => p.plan === 'free')
  return (
    <main id="main" className="pg se-pg privacy">
      <PageHead
        crumb={crumb}
        title={title}
        meta={project ? t('settings.privacy.meta', { project: project.name }) : undefined}
      />
      <div className="grid2">
        <div className="sections">
          <Section title={t('settings.erase.title')} meta={t('settings.erase.sub')}>
            <p className="se-p lede">{rich('settings.erase.body')}</p>
            {org.isPending ? (
              <Loading />
            ) : canErase ? (
              <div className="win">
                <div className="win-bar">
                  <b>{t('settings.erase.window')}</b>
                  <span>{t('settings.erase.windowSub')}</span>
                </div>
                <ScheduleForm orgId={orgId} projectId={projectId} />
              </div>
            ) : (
              <p className="se-hint">
                <Status s="miss" label={t('settings.adminOnly')} />
              </p>
            )}
          </Section>
          {canErase ? (
            <Section title={t('settings.erase.list')} meta={project?.name}>
              <Erasures orgId={orgId} projectId={projectId} />
            </Section>
          ) : null}
        </div>
        <div className="sections">
          {org.data ? (
            <Section
              title={t('settings.privacy.retention')}
              meta={t('settings.privacy.retentionMeta', { plan: t(PLAN_KEY[org.data.plan]) })}
            >
              <div className="kv">
                <span>{t('settings.privacy.detail')}</span>
                <span>{retention(t, org.data.retention_days)}</span>
                <span>{t('settings.privacy.crashes')}</span>
                <span>{retention(t, org.data.crash_retention_days)}</span>
                {/* D93: a symbol file stays while a crash may still need it. */}
                <span>{t('settings.privacy.symbols')}</span>
                <span>{t('settings.privacy.symbolsKeep')}</span>
                {org.data.plan === 'trial' && free ? (
                  <>
                    <span>{t('settings.privacy.afterTrial')}</span>
                    <span>
                      {t('settings.privacy.afterTrialKeep', {
                        detail: free.detail_retention_days,
                        crashes: free.crash_retention_days,
                      })}
                    </span>
                  </>
                ) : null}
              </div>
              {/* D94: reads stop at the current plan's retention. */}
              <p className="se-hint" style={{ paddingTop: 10 }}>
                {t('settings.privacy.shorter')}
              </p>
            </Section>
          ) : null}
          <Section title={t('settings.privacy.region')}>
            <div className="kv">
              <span>{t('settings.privacy.storedIn')}</span>
              <span>{t('settings.privacy.usEast')}</span>
            </div>
          </Section>
        </div>
      </div>
    </main>
  )
}
